import React, { useState, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { CsvReader } from "component/page/lead-import/csv-reader";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { LeadImport, NewLeadImport } from "model/lead-import";
import { saveLeadImport, reset } from "redux/slice/lead-import";
import { fetchLeads } from "redux/slice/leads";
import { fetchLeadTags } from "redux/slice/lead-tags";
import LinearProgress from "@mui/material/LinearProgress";
import { TableDisplay } from "component/page/lead-import/table-display";
import { validatedTableData, TableData } from "component/page/lead-import/utils/validatedTableData";
import {
	getLeadsOptimized,
	getAgentsWithLeads,
	getMarketsOptimized,
	getLeadImportsOptimized,
	getCampaignsOptimized,
} from "redux/selector";
import MuiAlert from "@mui/material/Alert";
import { styles } from "component/page/lead-import/style";
import { ImportError } from "type/import-error";
import { CSVLink as CsvLink } from "react-csv";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { StopImportConfirmDialog } from "component/shared/stop-import-confirm-dialog";
import { BypassImportDialog } from "./bypass-import-dialog";
import { formattedDateString } from "./utils";
import { User } from "model/user";
import {
	setRecentLeadImport,
	setLeadImportProgress,
	clearLeadImportProgress,
	isCancelLeadImportRequested,
	resetCancelLeadImport,
	requestCancelLeadImport,
} from "shared/lead-import-recent";
import classNames from "classnames";
import * as env from "shared/env";

const MemoTableDisplay = React.memo(TableDisplay);

interface Props extends WithStyles<typeof styles> {
	user: User;
	onImportComplete?: () => void;
	renderActions?: (params: {
		valid: boolean | undefined;
		submitted: boolean;
		inProgress: boolean;
		isStopping: boolean;
		progress: number;
		onImport: () => void;
		onCancel: () => void;
		onClear: () => void;
		onShowStopConfirm: () => void;
		onBypassImport: () => Promise<void>;
		importResultLength: number;
		renderCsvReader: (buttonLabel?: string) => React.ReactNode;
		validLeadsCount: number;
		invalidLeadsCount: number;
		hasImportPreview: boolean;
	}) => React.ReactNode;
}

const LeadImportContentComponent: React.FC<Props> = ({ user, classes = {}, renderActions, onImportComplete }) => {
	// Redux hooks
	const importResult = useSelector(getLeadImportsOptimized);
	const loading = useSelector((state: any) => state.leadImports.loading);
	const leads = useSelector(getLeadsOptimized);
	const markets = useSelector(getMarketsOptimized);
	const campaigns = useSelector(getCampaignsOptimized);
	const agents = useSelector(getAgentsWithLeads);
	const dispatch = useAppThunkDispatch();

	const [submitted, setSubmitted] = useState(false);
	const [importPreview, setImportPreview] = useState<NewLeadImport[]>([]);
	const [valid, setValid] = useState<boolean | undefined>(undefined);
	const [warning, setWarning] = useState<boolean | undefined>(undefined);
	const [importTotal, setImportTotal] = useState(0);
	const [processedCount, setProcessedCount] = useState(0);
	const [invalidHeaders, setInvalidHeaders] = useState<
		{ header: string; info: string }[] | undefined
	>(undefined);
	const [bypassModalOpen, setBypassModalOpen] = useState(false);
	const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
	const [isParsing, setIsParsing] = useState(false);
	const [isStopping, setIsStopping] = useState(false);
	const cancelRequestedRef = React.useRef<boolean>(false);
	const processedCountRef = React.useRef<number>(0);

	const updateProgress = useCallback(
		(processed: number, total: number, inProgressValue: boolean) => {
			setLeadImportProgress(user.clientId, {
				processed,
				total,
				inProgress: inProgressValue,
				updatedAt: Date.now(),
			});
		},
		[user.clientId]
	);

	// No longer need event listener - using localStorage for cancel state

	const tableData = useMemo(
		() =>
			validatedTableData(
				markets,
				leads,
				campaigns,
				agents,
				importPreview,
				true
			),
		[markets, leads, campaigns, agents, importPreview]
	);

	const validLeads = useMemo(
		() => tableData.filter((row: any) => !row.validationErrors && !row.duplicate),
		[tableData]
	);
	const invalidLeads = useMemo(
		() => tableData.filter((row: any) => row.validationErrors || row.duplicate),
		[tableData]
	);
	const validLeadsCount = validLeads.length;
	const invalidLeadsCount = invalidLeads.length;

	const totalToImport = importTotal || importPreview.length;
	const progress = () => (totalToImport ? (processedCount * 100) / totalToImport : 0);
	const inProgress = submitted;
	const hasImportActivity =
		importPreview.length > 0 || importResult.length > 0 || inProgress;
	const completed =
		!inProgress && totalToImport > 0 && processedCount >= totalToImport;

	const handleTableLoad = useCallback((validValue: boolean, warningValue: boolean) => {
		setValid(validValue);
		setWarning(warningValue);
	}, []);

	const previewTable = useMemo(() => {
		if (tableData.length === 0) return null;
		return (
			<MemoTableDisplay
				validate
				onLoad={handleTableLoad}
				tableData={tableData}
			/>
		);
	}, [tableData, handleTableLoad]);

	const clear = useCallback(() => {
		setImportPreview([]);
		setValid(undefined);
		setWarning(undefined);
		setInvalidHeaders(undefined);
		setImportTotal(0);
		setProcessedCount(0);
		setSubmitted(false);
		setIsParsing(false);
		cancelRequestedRef.current = false;
		processedCountRef.current = 0;
		clearLeadImportProgress(user.clientId);
		dispatch(reset());
	}, [dispatch, user.clientId]);

	React.useEffect(() => {
		if (renderActions && completed) {
			clear();
		}
	}, [renderActions, completed, clear]);


	const renderCsvReader = useCallback(
		(buttonLabel?: string) => (
			<CsvReader
				user={user}
				disabled={inProgress}
				buttonLabel={buttonLabel}
				onFileAccepted={() => setIsParsing(true)}
				onUpload={(results, invalidHeaders) => {
					clear();
					setInvalidHeaders(invalidHeaders);
					const leadImports: NewLeadImport[] = results.map((result: any) => {
						const leadImport: NewLeadImport = {
							id: Math.random().toString().split(".")[1],
							address: result.address,
							email: result.email,
							city: result.city,
							name: result.name,
							phone: result.phone,
							state: result.state,
							zip: result.zip,
							birthday: result.birthday
								? formattedDateString(result.birthday)
								: undefined,
							contactType: result.type,
							agentId: result.agentid ? parseInt(result.agentid) : (!user.admin && user.ownsLeads) ? user.agentId : undefined,
							transactionAnniversary: result.anniversary
								? formattedDateString(result.anniversary)
								: undefined,
							pipelineType: result.pipeline,
							tags: result.tags
								? result.tags.split(",").map((tag: string) => tag.trim())
								: undefined,
							note: result.note,
							textMessageOptIn:
								result.texting && result.texting.toLowerCase() === "true"
									? true
									: false,
							marketIds: result.marketids
								? result.marketids.split(",").map((market: string) => parseInt(market))
								: undefined,
							campaignIds: result.campaignids
								? result.campaignids
										.split(",")
										.map((campaign: string) => parseInt(campaign))
								: undefined,
							leadSource: result.source,
						};
						return leadImport;
					});
					setImportPreview([...leadImports]);
					setIsParsing(false);
				}}
			/>
		),
		[user, clear, inProgress]
	);

	// Template data
	const templateData = useCallback(() => {
		if (!user.admin && user.ownsLeads && user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures) {
			return "Name,Email,Phone,Address,City,State,Zip,Birthday,Type,Anniversary,Pipeline,Texting,Tags,CampaignIDs,MarketIDs,Note,Source";
		}
		if (user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures) {
			// Team CRM
			return "Name,Email,Phone,Address,City,State,Zip,Birthday,Type,AgentID,Anniversary,Pipeline,Texting,Tags,CampaignIDs,MarketIDs,Note";
		}
		if (user.permissions.crm) {
			return "Name,Email,Phone,Address,City,State,Zip,Birthday,Type,Anniversary,Pipeline,Texting,Tags,CampaignIDs,MarketIDs,Note,Source";
		}
		if (user.permissions.broker) {
			return "Name,Email,Phone,Address,City,State,Zip,AgentID,MarketIDs,Note,Source";
		}
		if (user.permissions.markets) {
			return "Name,Email,Phone,Address,City,State,Zip,MarketIDs,Note,Source";
		}
		return "";
	}, [user.permissions]);

	const leadImportSuccesses = importResult.filter(
		(leadImport) => !leadImport.error || leadImport.error === ImportError.action
	);
	const leadImportFails = importResult.filter(
		(leadImport) =>
			leadImport.error === ImportError.duplicate ||
			leadImport.error === ImportError.import
	);

	const successTableData = useMemo(
		() => validatedTableData(markets, leads, campaigns, agents, leadImportSuccesses, false),
		[markets, leads, campaigns, agents, leadImportSuccesses]
	);
	const failTableData = useMemo(
		() => validatedTableData(markets, leads, campaigns, agents, leadImportFails, false),
		[markets, leads, campaigns, agents, leadImportFails]
	);

	const save = useCallback(async () => {
		const chunkSize = 25;
		const successfulEmails: string[] = [];
		setSubmitted(true);
		setImportTotal(importPreview.length);
		resetCancelLeadImport(user.clientId);
		cancelRequestedRef.current = false;
		processedCountRef.current = 0;
		setProcessedCount(0);
		updateProgress(0, importPreview.length, true);
		dispatch(reset());
		let cancelled = false;
		for (let i = 0; i < importPreview.length; i += chunkSize) {
			const isCancelled =
				cancelRequestedRef.current || isCancelLeadImportRequested(user.clientId);
			if (isCancelled) {
				setIsStopping(true);
				cancelled = true;
				break;
			}
			let chunk = [...importPreview.slice(i, i + chunkSize)];
			const action = await dispatch(saveLeadImport({ leadImports: chunk }));
			const payload = (action as { payload?: LeadImport[] }).payload;
			payload
				?.filter(
					(leadImport) =>
						!leadImport.error || leadImport.error === ImportError.action
				)
				.forEach((leadImport) => {
					if (leadImport.email) {
						successfulEmails.push(leadImport.email);
					}
				});

			// Save progress incrementally so it's preserved on refresh
			// Mark as NOT completed so alert doesn't show during active import
			if (successfulEmails.length > 0) {
				setRecentLeadImport(user.clientId, {
					emails: successfulEmails,
					totalImported: successfulEmails.length,
					completedAt: Date.now(),
					dismissed: false,
					completed: false,
				});
			}

			processedCountRef.current += payload?.length ?? chunk.length;
			setProcessedCount(processedCountRef.current);
			updateProgress(processedCountRef.current, importPreview.length, true);
		}
		dispatch(fetchLeads());
		dispatch(fetchLeadTags());

		// Mark as completed so success alert shows
		if (successfulEmails.length > 0) {
			setRecentLeadImport(user.clientId, {
				emails: successfulEmails,
				totalImported: successfulEmails.length,
				completedAt: Date.now(),
				dismissed: false,
				completed: true,
			});
		}

		if (cancelled) {
			// Mark as completed so the banner persists after refresh showing partial import count
			if (successfulEmails.length > 0) {
				setRecentLeadImport(user.clientId, {
					emails: successfulEmails,
					totalImported: successfulEmails.length,
					completedAt: Date.now(),
					dismissed: false,
					completed: true,
				});
			}
			setSubmitted(false);
			setIsStopping(false);
			clearLeadImportProgress(user.clientId);
			onImportComplete?.();
			return;
		}
		setSubmitted(false);
		setIsStopping(false);
		clearLeadImportProgress(user.clientId);
		onImportComplete?.();
	}, [importPreview, dispatch, user.clientId, updateProgress, onImportComplete]);

	const performBypassImport = useCallback(async () => {
		const chunkSize = 25;
		const successfulEmails: string[] = [];
		setSubmitted(true);
		setBypassModalOpen(false);
		const leadsToImport = validLeads;
		setImportTotal(leadsToImport.length);
		resetCancelLeadImport(user.clientId);
		cancelRequestedRef.current = false;
		processedCountRef.current = 0;
		setProcessedCount(0);
		updateProgress(0, leadsToImport.length, true);
		dispatch(reset());
		let cancelled = false;
		// Don't modify importPreview - keep original data for display
		if (leadsToImport.length > 0) {
			for (let i = 0; i < leadsToImport.length; i += chunkSize) {
				if (cancelRequestedRef.current || isCancelLeadImportRequested(user.clientId)) {
					setIsStopping(true);
					cancelled = true;
					break;
				}
				let chunk = [...leadsToImport.slice(i, i + chunkSize)];
				const action = await dispatch(saveLeadImport({ leadImports: chunk }));
				const payload = (action as { payload?: LeadImport[] }).payload;
				payload
					?.filter(
						(leadImport) =>
							!leadImport.error || leadImport.error === ImportError.action
					)
					.forEach((leadImport) => {
						if (leadImport.email) {
							successfulEmails.push(leadImport.email);
						}
					});
				processedCountRef.current += payload?.length ?? chunk.length;
				setProcessedCount(processedCountRef.current);
				updateProgress(processedCountRef.current, leadsToImport.length, true);
			}
		}
		dispatch(fetchLeads());
		dispatch(fetchLeadTags());

		// Mark as completed so success alert shows
		if (successfulEmails.length > 0) {
			setRecentLeadImport(user.clientId, {
				emails: successfulEmails,
				totalImported: successfulEmails.length,
				completedAt: Date.now(),
				dismissed: false,
				completed: true,
			});
		}

		setSubmitted(false);
		setIsStopping(false);
		clearLeadImportProgress(user.clientId);
		onImportComplete?.();

		if (cancelled) {
			return;
		}
	}, [dispatch, validLeads, user.clientId, updateProgress, onImportComplete]);

	const handleBypassImport = useCallback(async () => {
		if (renderActions) {
			setBypassModalOpen(true);
		} else {
			await performBypassImport();
		}
	}, [renderActions, performBypassImport]);

	const handleCancelImport = useCallback(() => {
		if (inProgress) {
			setIsStopping(true);
			cancelRequestedRef.current = true;
			requestCancelLeadImport(user.clientId);
			return;
		}
		clear();
	}, [inProgress, clear, user.clientId]);

	const handleConfirmStop = useCallback(() => {
		setStopConfirmOpen(false);
		setIsStopping(true);
		cancelRequestedRef.current = true;
		requestCancelLeadImport(user.clientId);
	}, [user.clientId]);


	return (
		<Mui.Grid container spacing={2} direction="column" justifyContent="center">
			<Mui.Grid item>
				<Mui.Grid
					container
					direction="column"
					className={classNames(
						classes.uploadIntroContainer,
						importPreview.length > 0 && classes.hidden
					)}
				>
					<Mui.Grid item>
						<Mui.Typography
							className={classes.uploadIntroText}
						>
							Upload a CSV file to import leads into your account.
						</Mui.Typography>
					</Mui.Grid>
					{templateData() && (
						<Mui.Grid item>
							<Mui.Typography
								className={classes.templateLinkText}
							>
								<CsvLink
									data={templateData() || ""}
									filename="lead-import-template.csv"
									className={classes.csvLink}
								>
									Download CSV Template
								</CsvLink>
								<FeatherIcon className={classes.downloadIcon} color="secondary">
									<Icons.Download />
								</FeatherIcon>
							</Mui.Typography>
						</Mui.Grid>
					)}
					<Mui.Grid item>
						{renderCsvReader()}
					</Mui.Grid>
					{isParsing && (
						<Mui.Grid item>
							<Mui.Grid container alignItems="center" spacing={2}>
								<Mui.Grid item>
									<LoadingIndicator size={20} />
								</Mui.Grid>
								<Mui.Grid item>
									<Mui.Typography className={classes.parsingText}>
										Processing file...
									</Mui.Typography>
								</Mui.Grid>
							</Mui.Grid>
						</Mui.Grid>
					)}
				</Mui.Grid>
			</Mui.Grid>
			{importPreview.length > 0 && !renderActions && (
				<Mui.Grid item className={classes.importButtonsContainer}>
					<div className={classes.importButtons}>
						{progress() !== 100 && (
							<Mui.Grid container spacing={2} alignItems="center">
								{inProgress && importResult.length === 0 ? (
									<Mui.Grid item>
										<Mui.Grid container direction="row" alignItems="center" spacing={2}>
											<Mui.Grid item>
												<LoadingIndicator size={25} />
											</Mui.Grid>
											<Mui.Grid item>
												<span>Sending...</span>
											</Mui.Grid>
										</Mui.Grid>
									</Mui.Grid>
								) : (
									<>
										<Mui.Grid item>
											<Mui.Button
												variant="contained"
												color={"primary"}
												disabled={(valid !== undefined && !valid) || inProgress}
												onClick={save}
											>
												Import
											</Mui.Button>
										</Mui.Grid>
										<Mui.Grid item>
											<Mui.Button
												variant="contained"
												onClick={() => {
													if (inProgress) {
														setStopConfirmOpen(true);
													} else {
														clear();
													}
												}}
												disabled={isStopping}
												startIcon={isStopping ? <LoadingIndicator size={20} /> : undefined}
											>
											{isStopping
												? "Stopping... finishing current batch (up to 25)"
												: inProgress
													? "Stop"
													: valid
														? "Cancel"
														: "Upload File Again"}
											</Mui.Button>
										</Mui.Grid>
										<Mui.Grid item>
											{!valid && !inProgress && (
												<>
													<Mui.Button
														variant="contained"
														color={"primary"}
														onClick={() => setBypassModalOpen(true)}
														disabled={submitted || inProgress}
													>
														Bypass Errors & Import
													</Mui.Button>
												</>
											)}
										</Mui.Grid>
									</>
								)}
							</Mui.Grid>
						)}
						{progress() === 100 && <>{renderCsvReader("Import Again")}</>}
					</div>
				</Mui.Grid>
			)}
			{renderActions && renderActions({
				valid,
				submitted,
				inProgress,
				isStopping,
				progress: progress(),
				onImport: save,
				onCancel: handleCancelImport,
				onClear: clear,
				onShowStopConfirm: () => setStopConfirmOpen(true),
				onBypassImport: handleBypassImport,
				importResultLength: importResult.length,
				renderCsvReader,
				validLeadsCount,
				invalidLeadsCount,
				hasImportPreview: hasImportActivity
			})}
			<StopImportConfirmDialog
				open={stopConfirmOpen}
				onCancel={() => setStopConfirmOpen(false)}
				onConfirm={handleConfirmStop}
			/>
			<BypassImportDialog
				open={bypassModalOpen}
				validCount={validLeadsCount}
				invalidCount={invalidLeadsCount}
				onCancel={() => setBypassModalOpen(false)}
				onConfirm={performBypassImport}
			/>
			{importPreview.length > 0 && importResult.length === 0 && !inProgress && (
				<Mui.Grid item className={classes.fullWidth}>
					<Mui.Grid container spacing={2} direction="column">
						<Mui.Grid item>
							<Mui.Typography variant="h2">Import Preview</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item className={classes.fullWidth}>
							{invalidHeaders && invalidHeaders.length > 0 && (
								<MuiAlert severity="warning" className={classes.alert}>
									{`Warning! There are columns in your data that are not supported and will not be imported:`}
									{invalidHeaders.map((value, index) => {
										if (value.header.length > 0) {
											return (
												<Mui.Tooltip
													title={value.info}
													classes={{ tooltip: classes.tooltip }}
													key={index}
												>
													<Mui.Chip
														className={classes.invalidHeaderChip}
														size="small"
														label={value.header}
														icon={
															<FeatherIcon fontSize="small">
																<Icons.Info />
															</FeatherIcon>
														}
													/>
												</Mui.Tooltip>
											);
										}
										return null;
									})}
								</MuiAlert>
							)}
							{valid !== undefined && !valid && (
								<MuiAlert severity="error" className={classes.alert}>
									Oops! We found some errors. Correct the highlighted issues below and
									upload again.
								</MuiAlert>
							)}
							{valid && !warning && (
								<MuiAlert severity="success" className={classes.alert}>
									Looks Good! The data has passed our tests. Continue to import.
								</MuiAlert>
							)}
							{valid && warning && (
								<MuiAlert severity="warning" className={classes.alert}>
									The data can be imported but we found some potential issues. Please
									review the highlighted issues before you import.
								</MuiAlert>
							)}
							{previewTable}
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			)}
			{(inProgress || importResult.length > 0) && (
				<Mui.Grid item>
					{totalToImport > 0 && (
						<LinearProgress
							color="secondary"
							variant="determinate"
							value={progress()}
						/>
					)}
					{totalToImport > 0 && (
						<Mui.Grid container direction="column" spacing={2}>
							<Mui.Grid item>
								{`${loading ? "loading..." : progress() === 100 ? "Import Complete:" : "Import Progress:"}`}
								<Mui.Typography>
									{`${processedCount} of ${totalToImport} Imported`}
								</Mui.Typography>
							</Mui.Grid>
						</Mui.Grid>
					)}
				</Mui.Grid>
			)}
			{leadImportFails.length > 0 && (
				<Mui.Grid item>
					<Mui.Grid container spacing={2} direction="column">
						<Mui.Grid item>
							<Mui.Typography variant="h2">Failed To Import:</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<MemoTableDisplay tableData={failTableData} />
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			)}
			{leadImportSuccesses.length > 0 && progress() === 100 && (
				<Mui.Grid item>
					<Mui.Grid container spacing={2} direction="column">
						<Mui.Grid item>
							<Mui.Typography variant="h2">Import Successful:</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<MemoTableDisplay tableData={successTableData} />
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			)}
		</Mui.Grid>
	);
};

export const LeadImportContent = withStyles(LeadImportContentComponent, styles);
